// src/components/impact/ImpactFutureVision.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { renderIcon } from '@dopomogai/utils';
import clsx from 'clsx';

interface Pillar {
  icon: string;
  title: string;
  text: string;
}

interface VisionData {
  headline: string;
  pillars: Pillar[];
}

interface ImpactFutureVisionProps {
  visionData: VisionData;
}

export const ImpactFutureVision: React.FC<ImpactFutureVisionProps> = ({ visionData }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  return (
    <section className="bg-gray-900 text-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(#2e3c51_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#000_60%,transparent_100%)]"></div>
        <div className="container mx-auto px-4 relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5 }}
              className="text-center max-w-3xl mx-auto"
            >
                <h2 className="text-3xl md:text-4xl font-bold">{visionData.headline}</h2>
            </motion.div>
            <motion.div 
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 max-w-6xl mx-auto"
            >
                {visionData.pillars.map((pillar, index) => (
                    // --- THE FIX: This is now the animator and the 'group' container ---
                    <motion.div 
                      key={index} 
                      variants={itemVariants}
                      className="relative group h-full"
                    >
                      {/* Layer 1: The Shadow. Positioned absolutely behind the content. */}
                      <div 
                        className={clsx(
                          "absolute inset-0 bg-slate-700 rounded-xl",
                          "transition-transform duration-200 ease-out group-hover:translate-x-1 group-hover:translate-y-1"
                        )}
                      />

                      {/* Layer 2: The Content. This is the visible card that moves on hover. */}
                      <div 
                        className={clsx(
                          "relative bg-slate-800/50 backdrop-blur-sm p-8 rounded-xl border border-slate-700",
                          "text-center flex flex-col items-center h-full",
                          "transition-transform duration-200 ease-out group-hover:-translate-x-1 group-hover:-translate-y-1"
                        )}
                      >
                          <div className="p-4 bg-emerald-500/10 border-2 border-emerald-500/30 rounded-full mb-5">
                            {renderIcon(pillar.icon, { size: 32, className: "text-emerald-400" })}
                          </div>
                          <h3 className="text-xl font-bold text-white mb-2">{pillar.title}</h3>
                          <p className="text-slate-300">{pillar.text}</p>
                      </div>
                    </motion.div>
                ))}
            </motion.div>
        </div>
    </section>
  );
};

